import {
  ChangeEvent,
  FC,
  Fragment,
  MouseEvent,
  useMemo,
  useState,
} from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  Button,
  InlineNotification,
  Link,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import { StatusTag } from 'components/status-tag';
import { WellWorkflowStatusBadge } from 'pages/afe-catalog/structuring/well-workflow-status-badge';
import { fmtAmount } from '../format';
import {
  fetchLineItemOverviewAtom,
  lineItemOverviewDataAtom,
  lineItemOverviewErrorAtom,
  lineItemOverviewLoadingAtom,
  lineItemOverviewUrl,
} from './atoms/line-items';
import {
  deriveLineItemSectionStatus,
  formatProductionPeriod,
} from './line-item-utils';
import {
  READ_ONLY_LINE_ITEM_HEADERS,
  renderReadOnlyLineItemCells,
} from './line-item-cells';
import type { LineItemGroup, LineItemRow } from './types-line-item';
import shell from 'pages/afe-catalog/structuring/structuring-module.module.scss';
import local from './line-item-review.module.scss';

interface Props {
  documentId: number | string;
  // Drill into a well's line items — the argument is the group's index in the
  // (unfiltered) overview list, which the shell uses to drive the drill-in.
  onSelectWell: (index: number) => void;
}

const WELL_GROUP_HEADERS = [
  'Status',
  'Well Name',
  'UWI / API',
  'County / State',
  'Products',
  'Production Period',
  '# Line Items',
  'Total Owner Value',
];

// UWI hyperlink target (same /wells/:uwi page the catalogs use).
const wellUrl = (uwi: string) => `/wells/${uwi}`;

export const LineItemReviewSection: FC<Props> = ({
  documentId,
  onSelectWell,
}) => {
  const overview = useAtomValue(lineItemOverviewDataAtom);
  const loading = useAtomValue(lineItemOverviewLoadingAtom);
  const error = useAtomValue(lineItemOverviewErrorAtom);
  const reload = useSetAtom(fetchLineItemOverviewAtom);

  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const groups = useMemo(() => overview?.groups ?? [], [overview?.groups]);
  const summary = overview?.summary;

  const refresh = () => reload(lineItemOverviewUrl(documentId));

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Keep each group's ORIGINAL index so a filtered row still drills into the
  // right well (the shell selects by index into the unfiltered list).
  const indexed = useMemo(
    () => groups.map((group, index) => ({ group, index })),
    [groups]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return indexed;
    return indexed.filter(({ group }) =>
      `${group.wellName ?? ''} ${group.uwi ?? ''} ${
        group.propertyNumber ?? ''
      } ${group.countyState ?? ''}`
        .toLowerCase()
        .includes(query)
    );
  }, [indexed, search]);

  const renderPreview = (lineItems: LineItemRow[]) => {
    if (lineItems.length === 0) {
      return (
        <p>
          No line items for this well. Line items are derived from the wells
          acknowledged in Well Information.
        </p>
      );
    }
    return (
      <div style={{ overflowX: 'auto' }}>
        <Table size="sm">
          <TableHead>
            <TableRow>
              {READ_ONLY_LINE_ITEM_HEADERS.map((header) => (
                <TableHeader key={header}>{header}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.map((row) => (
              <TableRow
                key={row.id}
                className={
                  row.status === 'removed' ? shell.rowRemoved : undefined
                }
              >
                {renderReadOnlyLineItemCells(row)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderGroupRow = (group: LineItemGroup, index: number) => {
    // TableExpandRow omits onClick (@carbon/react), so the drill-in click lives
    // on the data cells; the auto expand-chevron cell + the UWI link are excluded.
    const drill = {
      onClick: () => onSelectWell(index),
      style: { cursor: 'pointer' as const },
    };
    return (
      <Fragment key={group.key}>
        <TableExpandRow
          aria-label={`Expand line items for ${group.wellName ?? 'well'}`}
          isExpanded={expanded.has(group.key)}
          onExpand={() => toggleExpanded(group.key)}
        >
          <TableCell {...drill}>
            <WellWorkflowStatusBadge status={group.status} variant="inline" />
          </TableCell>
          <TableCell {...drill}>{group.wellName ?? '—'}</TableCell>
          <TableCell {...drill}>
            {group.uwi ? (
              <Link
                href={wellUrl(group.uwi)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: MouseEvent) => e.stopPropagation()}
              >
                {group.uwi}
              </Link>
            ) : (
              '—'
            )}
          </TableCell>
          <TableCell {...drill}>{group.countyState ?? '—'}</TableCell>
          <TableCell {...drill}>
            {group.products.length ? group.products.join(' ') : '—'}
          </TableCell>
          <TableCell {...drill}>
            {formatProductionPeriod(
              group.productionMonthStart,
              group.productionMonthEnd
            )}
          </TableCell>
          <TableCell {...drill}>{group.lineItemCount}</TableCell>
          <TableCell {...drill}>{fmtAmount(group.totalOwnerValue)}</TableCell>
        </TableExpandRow>
        {expanded.has(group.key) && (
          <TableExpandedRow colSpan={WELL_GROUP_HEADERS.length + 1}>
            {renderPreview(group.lineItems)}
          </TableExpandedRow>
        )}
      </Fragment>
    );
  };

  return (
    <div className={`${shell.overviewBody} ${local.expandMatchApp}`}>
      <div className={shell.overviewSectionTitleRow}>
        <h4 className={shell.overviewSectionTitle}>Line Item Review</h4>
        {summary && (
          <StatusTag
            status={deriveLineItemSectionStatus(summary)}
            progress={`${summary.confirmed}/${summary.total}`}
          />
        )}
      </div>

      {/* Reload-failed-with-stale-data banner (the no-data case goes to the
          dedicated error branch below so a 5xx never reads as an empty check). */}
      {error && overview && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Unable to load line items"
          subtitle={error}
        />
      )}

      {loading && !overview ? (
        <Loading withOverlay={false} small description="Loading line items…" />
      ) : error && !overview ? (
        <div>
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Unable to load line items"
            subtitle="Something went wrong loading this section."
            style={{ maxWidth: '100%' }}
          />
          <Button kind="tertiary" size="sm" onClick={refresh}>
            Retry
          </Button>
        </div>
      ) : groups.length === 0 ? (
        <p>No line items on this check.</p>
      ) : (
        <TableContainer>
          <TableToolbar size="sm">
            <TableToolbarContent>
              <TableToolbarSearch
                value={search}
                onChange={(event: ChangeEvent<HTMLInputElement> | string) =>
                  setSearch(
                    typeof event === 'string' ? event : event.target.value
                  )
                }
                placeholder="Search by well name, UWI, or property…"
                persistent
                size="sm"
              />
            </TableToolbarContent>
          </TableToolbar>
          <Table size="sm">
            <TableHead>
              <TableRow>
                <TableExpandHeader aria-label="Expand row" />
                {WELL_GROUP_HEADERS.map((header) => (
                  <TableHeader key={header}>{header}</TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(({ group, index }) => renderGroupRow(group, index))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};
